import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { X } from "lucide-react";

import {
  useLazyGetContractFamilySummaryGridCellQuery,
  useLazyResolveContractFamilyPipelineCitationQuery,
  useOverrideContractFamilyStagedValueMutation,
} from "@/api/contract-family-pipeline";
import { Button } from "@/components/ui/button";
import { useGetBusinessReportOutcomeQuery } from "@/modules/snapshots/businessReports.api";
import { ResultDetailsSection } from "@/modules/snapshots/resultDetails/ResultDetailsSection";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import type { PipelineCitationTarget } from "@/types/contract-family-pipeline";
import { formatPipelineCitationText } from "@/views/contract-families/pipeline/pipelineCitationText";
import { buildPipelineReturnPath } from "@/views/contract-families/pipeline/stageNavigation";
import type { PipelineSummaryGridRowKind } from "./pipelinePreview.types";
import type { StagedValue } from "@/types/contract-family-pipeline";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runId: string;
  rowKind: PipelineSummaryGridRowKind;
  rowId: string;
  stagedOutcomeId: string | null;
  fieldId: string;
  readOnly: boolean;
  folderFseId?: string | null;
  onSaved?: (value: StagedValue) => void;
}

interface SummaryValueForm {
  value: string;
}

const MAX_SUMMARY_VALUE_LENGTH = 100_000;
const GridSidebarPdfViewer = React.lazy(async () => {
  const module = await import("@/modules/snapshots/grid/sidebar/pdfViewer");
  return { default: module.GridSidebarPdfViewer };
});

const safeSaveError = "Unable to save this value. Please try again.";
const safeRevertError = "Unable to revert this value. Please try again.";

export const PipelinePreviewDrawer = ({
  open,
  onOpenChange,
  runId,
  rowKind,
  rowId,
  stagedOutcomeId,
  fieldId,
  readOnly,
  folderFseId,
  onSaved,
}: Props) => {
  const [loadCell, cellQuery] = useLazyGetContractFamilySummaryGridCellQuery();
  const [overrideValue, overrideState] = useOverrideContractFamilyStagedValueMutation();
  const [resolveCitation] = useLazyResolveContractFamilyPipelineCitationQuery();
  const [citationTarget, setCitationTarget] = React.useState<PipelineCitationTarget | null>(null);
  const [citationError, setCitationError] = React.useState(false);
  const sourceOutcome = useGetBusinessReportOutcomeQuery(citationTarget?.outcome_id ?? "", {
    skip: citationTarget === null,
  });
  const { handleCitationClick } = useCurrentPdf();
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const returnFocusRef = React.useRef<HTMLElement | null>(null);
  const { register, handleSubmit, reset, watch } = useForm<SummaryValueForm>({ defaultValues: { value: "" } });
  const currentValue = watch("value");
  const isCellLoading = cellQuery.isLoading || cellQuery.isFetching;
  // `data` may remain populated with the previous selector while a lazy query fetches.
  // `currentData` is scoped to the active selector, so never expose stale detail state.
  const detail = isCellLoading ? undefined : (cellQuery.currentData ?? cellQuery.data);
  const detailFieldId = detail?.field_id;
  const isSummary = rowKind === "family_summary";
  const canEdit = isSummary && Boolean(detail?.editable) && !readOnly && stagedOutcomeId !== null;
  const formattedGeneratedValue = formatPipelineCitationText(detail?.generated_value ?? "", detail?.citations ?? []);
  const formattedOverrideValue =
    detail?.override_value === null
      ? null
      : formatPipelineCitationText(detail?.override_value ?? "", detail?.citations ?? []);
  const formattedEffectiveValue = formatPipelineCitationText(detail?.effective_value ?? "", detail?.citations ?? []);
  const originalValue = formattedOverrideValue ?? formattedGeneratedValue;
  const isDuplicate = currentValue.trim() === originalValue.trim();
  const isSaveDisabled = overrideState.isLoading || !canEdit || currentValue.trim().length === 0 || isDuplicate;
  const supportingDocumentActions = React.useMemo(() => {
    const seenSourceOutcomeIds = new Set(detail?.source_outcome_id ? [detail.source_outcome_id] : []);
    const seenFseIds = new Set(detail?.fse_id ? [detail.fse_id] : []);

    return (detail?.supporting_documents ?? []).map(document => {
      const showOpenDocument =
        document.source_outcome_id !== null && !seenSourceOutcomeIds.has(document.source_outcome_id);
      const showQa = document.fse_id !== null && !seenFseIds.has(document.fse_id);
      if (document.source_outcome_id !== null) seenSourceOutcomeIds.add(document.source_outcome_id);
      if (document.fse_id !== null) seenFseIds.add(document.fse_id);
      return { document, showOpenDocument, showQa };
    });
  }, [detail?.fse_id, detail?.source_outcome_id, detail?.supporting_documents]);

  React.useEffect(() => {
    const source = sourceOutcome.data;
    const fseId = source?.fs_entry?.id;
    if (!citationTarget || !source || !fseId) return;
    const page = citationTarget.page_ranges[0]?.[0];
    handleCitationClick({
      citationId: `${citationTarget.outcome_id}:${page ?? ""}`,
      page,
      highlights: [],
      activeHighlightIndex: 0,
      fseId,
      documentId: citationTarget.original_document_id,
      citation: page ?? 1,
      index: 0,
    });
  }, [citationTarget, handleCitationClick, sourceOutcome.data]);

  React.useEffect(() => {
    if (!open) return;
    setSaveError(null);
    void loadCell({ runId, rowKind, rowId, fieldId });
  }, [fieldId, loadCell, open, rowId, rowKind, runId]);

  React.useEffect(() => {
    if (!open || detailFieldId === undefined) return;
    reset({ value: originalValue });
  }, [detailFieldId, open, originalValue, reset]);

  const save = React.useCallback(
    async ({ value }: SummaryValueForm) => {
      const trimmedValue = value.trim();
      if (!canEdit || !trimmedValue || trimmedValue === originalValue.trim()) return;
      setSaveError(null);
      try {
        const saved = await overrideValue({
          runId,
          stagedOutcomeId,
          fieldId,
          value: trimmedValue,
          summaryRowId: rowId,
        }).unwrap();
        onSaved?.(saved);
      } catch {
        setSaveError(safeSaveError);
      }
    },
    [canEdit, fieldId, onSaved, originalValue, overrideValue, rowId, runId, stagedOutcomeId],
  );

  const revert = React.useCallback(async () => {
    if (!canEdit || detail?.override_value === null) return;
    setSaveError(null);
    try {
      const saved = await overrideValue({ runId, stagedOutcomeId, fieldId, value: null, summaryRowId: rowId }).unwrap();
      onSaved?.(saved);
    } catch {
      setSaveError(safeRevertError);
    }
  }, [canEdit, detail?.override_value, fieldId, onSaved, overrideValue, rowId, runId, stagedOutcomeId]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) returnFocusRef.current?.focus();
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange} modal>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-950/35" />
        <Dialog.Content
          className="fixed inset-y-0 right-0 z-50 w-full overflow-hidden border-l border-slate-200 bg-white shadow-2xl outline-none md:w-[70%]"
          onOpenAutoFocus={() => {
            returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          }}
          onCloseAutoFocus={event => {
            event.preventDefault();
            returnFocusRef.current?.focus();
          }}
        >
          <div className="flex h-full min-h-0 flex-col">
            <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
              <div>
                <Dialog.Title className="text-base font-semibold text-slate-950">
                  {detail?.field_name ?? "Cell details"}
                </Dialog.Title>
                <Dialog.Description className="mt-1 text-sm text-slate-600">
                  Review the generated value and its authorized evidence.
                </Dialog.Description>
              </div>
              <Dialog.Close asChild>
                <Button type="button" variant="ghost" size="icon" aria-label="Close cell details">
                  <X className="h-4 w-4" />
                </Button>
              </Dialog.Close>
            </header>
            <div className="flex min-h-0 flex-1">
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
                {isCellLoading ? <p className="text-sm text-slate-600">Loading cell details…</p> : null}
                {cellQuery.isError ? (
                  <p role="alert" className="text-sm text-red-700">
                    Unable to load this cell.
                  </p>
                ) : null}
                {detail && !cellQuery.isError ? (
                  <div className="space-y-6">
                    {isSummary ? (
                      <form className="space-y-3" onSubmit={handleSubmit(save)}>
                        <label className="grid gap-1.5 text-sm font-medium text-slate-800">
                          Summary value
                          <textarea
                            aria-label="Summary value"
                            className="min-h-36 rounded-md border border-slate-300 p-3 font-normal text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral disabled:bg-slate-50"
                            disabled={!canEdit}
                            maxLength={MAX_SUMMARY_VALUE_LENGTH}
                            onInput={event => {
                              if (event.currentTarget.value.length > MAX_SUMMARY_VALUE_LENGTH) {
                                event.currentTarget.value = event.currentTarget.value.slice(
                                  0,
                                  MAX_SUMMARY_VALUE_LENGTH,
                                );
                              }
                            }}
                            {...register("value", { maxLength: MAX_SUMMARY_VALUE_LENGTH })}
                          />
                        </label>
                        {detail.generated_value !== null && detail.override_value !== null ? (
                          <section aria-label="Generated value" className="rounded-md bg-slate-50 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Generated value
                            </p>
                            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-800">
                              {formattedGeneratedValue}
                            </p>
                          </section>
                        ) : null}
                        <ResultDetailsSection details={detail.details} />
                        {canEdit ? (
                          <div className="flex flex-wrap gap-2">
                            <Button type="submit" disabled={isSaveDisabled}>
                              Save changes
                            </Button>
                            {detail.override_value !== null ? (
                              <Button
                                type="button"
                                variant="outline"
                                disabled={overrideState.isLoading}
                                onClick={() => void revert()}
                              >
                                Revert to generated
                              </Button>
                            ) : null}
                          </div>
                        ) : null}
                        {saveError ? (
                          <p role="alert" className="text-sm text-red-700">
                            {saveError}
                          </p>
                        ) : null}
                      </form>
                    ) : (
                      <section aria-label="Source value">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Source value</p>
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-900">
                          {formattedEffectiveValue}
                        </p>
                      </section>
                    )}
                    <section aria-label="Citations">
                      <h3 className="text-sm font-semibold text-slate-900">Citations</h3>
                      {detail.citations.length ? (
                        <ul className="mt-2 space-y-2">
                          {detail.citations.map((citation, index) => (
                            <li
                              key={`${citation.document_id}:${citation.page ?? ""}:${index}`}
                              className="rounded-md bg-slate-50 p-3 text-sm text-slate-800"
                            >
                              <p className="whitespace-pre-wrap break-words">
                                {citation.text ?? "Citation text is unavailable."}
                              </p>
                              {citation.page !== null ? (
                                <p className="mt-1 text-xs text-slate-500">Page {citation.page}</p>
                              ) : null}
                              {detail.staged_value_id ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="mt-2"
                                  aria-label={`Citation ${index + 1}`}
                                  onClick={() => {
                                    setCitationError(false);
                                    void resolveCitation({
                                      runId,
                                      stagedValueId: detail.staged_value_id!,
                                      citationIndex: index,
                                    })
                                      .unwrap()
                                      .then(setCitationTarget)
                                      .catch(() => setCitationError(true));
                                  }}
                                >
                                  Open cited PDF
                                </Button>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-1 text-sm text-slate-600">No citations are available for this value.</p>
                      )}
                      {citationError ? (
                        <p role="alert" className="mt-2 text-sm text-red-700">
                          Source document is unavailable
                        </p>
                      ) : null}
                    </section>
                    <section aria-label="Documents" className="space-y-2">
                      <h3 className="text-sm font-semibold text-slate-900">Documents</h3>
                      {detail.source_outcome_id ? (
                        <Link
                          className="block text-sm font-medium underline"
                          to={`/reports/outcome/${encodeURIComponent(detail.source_outcome_id)}`}
                        >
                          Open document
                        </Link>
                      ) : null}
                      {detail.fse_id ? (
                        <Link
                          className="block text-sm font-medium underline"
                          to={`/chat/${encodeURIComponent(detail.fse_id)}`}
                          state={{ fromSnapshot: buildPipelineReturnPath(folderFseId, runId, "summarise") }}
                        >
                          Go to Q&A
                        </Link>
                      ) : null}
                      {supportingDocumentActions.map(({ document, showOpenDocument, showQa }) => (
                        <div key={document.document_id} className="rounded-md border border-slate-200 p-3">
                          <p className="break-words text-sm font-medium text-slate-900">{document.name}</p>
                          {document.role ? <p className="text-xs text-slate-500">{document.role}</p> : null}
                          {showOpenDocument && document.source_outcome_id ? (
                            <Link
                              className="mt-2 inline-block text-sm underline"
                              to={`/reports/outcome/${encodeURIComponent(document.source_outcome_id)}`}
                            >
                              Open document
                            </Link>
                          ) : null}
                          {showQa && document.fse_id ? (
                            <Link
                              className="ml-3 mt-2 inline-block text-sm underline"
                              to={`/chat/${encodeURIComponent(document.fse_id)}`}
                              state={{ fromSnapshot: buildPipelineReturnPath(folderFseId, runId, "summarise") }}
                            >
                              Go to Q&A
                            </Link>
                          ) : null}
                        </div>
                      ))}
                    </section>
                  </div>
                ) : null}
              </div>
              {citationTarget && sourceOutcome.data ? (
                <aside
                  aria-label="PDF document preview"
                  className="hidden min-h-0 w-1/2 border-l border-slate-200 lg:flex"
                >
                  <React.Suspense fallback={<p className="p-4 text-sm text-slate-600">Loading PDF…</p>}>
                    <GridSidebarPdfViewer />
                  </React.Suspense>
                </aside>
              ) : null}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
